import { useState } from 'react';
import {
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconCheck, IconSearch } from '@tabler/icons-react';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import type { ApiData } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { formatPhone } from '../../-components/format';

type AvailableNumber = ApiData<(typeof api)['phone-numbers']['search']['get']>[number];

const numberTypes = ['local', 'toll_free', 'mobile'] as const;

type NumberType = (typeof numberTypes)[number];

const numberTypeOptions = [
  { value: 'local', label: 'Local' },
  { value: 'toll_free', label: 'Toll Free' },
  { value: 'mobile', label: 'Mobile' },
];

const countryOptions = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
];

function toNumberType(value: string | null) {
  return numberTypes.find((type) => type === value) ?? null;
}

function PurchaseConfirmModal({
  number,
  isPurchasing,
  onCancel,
  onConfirm,
}: {
  number: AvailableNumber;
  isPurchasing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      opened
      onClose={() => {
        if (!isPurchasing) onCancel();
      }}
      closeOnClickOutside={!isPurchasing}
      closeOnEscape={!isPurchasing}
      title="Confirm Purchase"
    >
      <Stack>
        <Text fw={600} size="lg" ta="center">
          {formatPhone(number.phoneNumber)}
        </Text>
        <Paper p="md" radius="md" withBorder>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text c="dimmed">Upfront charge</Text>
              <Text fw={500}>
                {number.currency} {number.upfrontPrice}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text c="dimmed">Recurring monthly</Text>
              <Text fw={500}>
                {number.currency} {number.monthlyPrice}/mo
              </Text>
            </Group>
          </Stack>
        </Paper>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onCancel} disabled={isPurchasing}>
            Cancel
          </Button>
          <Button color="green" onClick={onConfirm} loading={isPurchasing}>
            Confirm Purchase
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function SearchNumbersModal({
  opened,
  organizationId,
  onClose,
}: {
  opened: boolean;
  organizationId: string;
  onClose: () => void;
}) {
  const [countryCode, setCountryCode] = useState('US');
  const [areaCode, setAreaCode] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [numberType, setNumberType] = useState<NumberType | null>('local');
  const [searchLimit, setSearchLimit] = useState<string | number>(20);
  const [availableNumbers, setAvailableNumbers] = useState<AvailableNumber[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmNumber, setConfirmNumber] = useState<AvailableNumber | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);

  const close = () => {
    setAvailableNumbers([]);
    setError(null);
    onClose();
  };

  const search = async () => {
    setIsSearching(true);
    setError(null);

    const result = await api['phone-numbers'].search.get({
      query: {
        organizationId,
        countryCode,
        areaCode: areaCode || undefined,
        state: stateCode || undefined,
        phoneNumberType: numberType ?? undefined,
        limit: typeof searchLimit === 'number' ? searchLimit : 20,
      },
    });

    setIsSearching(false);

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to search for numbers'));

    setAvailableNumbers(result.data);
  };

  const purchase = async () => {
    if (!confirmNumber) return;

    setIsPurchasing(true);
    setError(null);

    const result = await api['phone-numbers'].purchase.post({
      organizationId,
      phoneNumber: confirmNumber.phoneNumber,
    });

    setIsPurchasing(false);

    if (result.error) return setError(getErrorMessage(result.error, 'Failed to purchase number'));

    setAvailableNumbers((previous) => previous.filter((n) => n.phoneNumber !== confirmNumber.phoneNumber));
    setConfirmNumber(null);
  };

  return (
    <>
      <Modal opened={opened} onClose={close} title="Search Available Numbers" size="lg">
        <Stack gap="md">
          <ErrorAlert message={error} />

          <SimpleGrid cols={2}>
            <Select
              label="Country"
              value={countryCode}
              onChange={(value) => setCountryCode(value ?? 'US')}
              data={countryOptions}
            />
            <Select
              label="Number Type"
              value={numberType}
              onChange={(value) => setNumberType(toNumberType(value))}
              data={numberTypeOptions}
            />
          </SimpleGrid>
          <SimpleGrid cols={3}>
            <TextInput
              label="Area Code"
              placeholder="e.g. 415"
              value={areaCode}
              onChange={(event) => setAreaCode(event.currentTarget.value)}
            />
            <TextInput
              label="State/Province"
              placeholder="e.g. CA"
              value={stateCode}
              onChange={(event) => setStateCode(event.currentTarget.value)}
            />
            <NumberInput label="Results" value={searchLimit} onChange={setSearchLimit} min={1} max={50} />
          </SimpleGrid>
          <Button leftSection={<IconSearch size={16} />} onClick={() => void search()} loading={isSearching}>
            Search
          </Button>

          {availableNumbers.length > 0 && (
            <Paper radius="md" withBorder style={{ maxHeight: 400, overflow: 'auto' }}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Number</Table.Th>
                    <Table.Th>Monthly</Table.Th>
                    <Table.Th w={100} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {availableNumbers.map((number) => (
                    <Table.Tr key={number.phoneNumber}>
                      <Table.Td>
                        <Text fw={500}>{formatPhone(number.phoneNumber)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {number.currency} {number.monthlyPrice}/mo
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="light"
                          color="green"
                          leftSection={<IconCheck size={14} />}
                          onClick={() => setConfirmNumber(number)}
                          disabled={isPurchasing}
                        >
                          Buy
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </Stack>
      </Modal>

      {confirmNumber && (
        <PurchaseConfirmModal
          number={confirmNumber}
          isPurchasing={isPurchasing}
          onCancel={() => setConfirmNumber(null)}
          onConfirm={() => void purchase()}
        />
      )}
    </>
  );
}
